import type { Terminal, TerminalKind } from "./entities.ts";

export type ConnectionSeverity = "ok" | "warning" | "blocked";

export type ConnectionValidation = {
  severity: ConnectionSeverity;
  reason?: string;
};

const ALLOWED: Readonly<Record<TerminalKind, ReadonlySet<TerminalKind>>> = {
  phase: new Set<TerminalKind>(["phase", "neutral"]),
  neutral: new Set<TerminalKind>(["neutral", "phase"]),
  earth: new Set<TerminalKind>(["earth"]),
  control: new Set<TerminalKind>(["control", "signal"]),
  signal: new Set<TerminalKind>(["signal", "control"]),
};

/**
 * Hård regel: earth-to-phase är alltid BLOCKED.
 * Mjuka regler: mismatched phase-labels (L1-L2) ger WARNING.
 * UI får visa röd blixt men tillåta kopplingen så användaren kan rätta.
 */
export function validateConnection(
  from: Pick<Terminal, "kind" | "label">,
  to: Pick<Terminal, "kind" | "label">,
): ConnectionValidation {
  // Hård blockering: earth kan bara till earth.
  if (from.kind === "earth" && to.kind !== "earth") {
    return { severity: "blocked", reason: "Jord får bara kopplas till jord." };
  }
  if (to.kind === "earth" && from.kind !== "earth") {
    return { severity: "blocked", reason: "Jord får bara kopplas till jord." };
  }

  // Phase-kontroll: L1/L2/L3 bör matcha.
  if (from.kind === "phase" && to.kind === "phase") {
    const fp = phaseTag(from.label);
    const tp = phaseTag(to.label);
    if (fp && tp && fp !== tp) {
      return { severity: "warning", reason: `Fas-mismatch: ${fp} mot ${tp}.` };
    }
  }

  // Mixning av control och power är dåligt men inte blockerat.
  const allowed = ALLOWED[from.kind];
  if (!allowed.has(to.kind)) {
    return {
      severity: "warning",
      reason: `Ovanlig koppling: ${from.kind} → ${to.kind}.`,
    };
  }

  return { severity: "ok" };
}

const PHASE_RE = /^L[1-3]$/i;
function phaseTag(label: string): string | null {
  return PHASE_RE.test(label) ? label.toUpperCase() : null;
}
